// Declarative description of the assessment report buttons.
// Pro (simulation): the buttons only depend on the context.
// Labs: legacy rules, kept until the Labs report gets its own design.

export type ReportContext =
  | 'editor'    // analytics dialog in the editor (trainer)
  | 'player'    // end of session, in the player (regular or LMS launch, see isLti)
  | 'program-launcher'; // past session reviewed from the program launcher (during or after a session)

export interface ReportFacts {
  context: ReportContext;
  isLti: boolean; // player launched from an LMS
  readOnly: boolean; // report link reopened later (e.g. from the LMS gradebook): review only
  isSimulation: boolean;
  hasResult: boolean;
  canEdit: boolean;
  hasClickedAssessmentButton: boolean;
  hasExternalLink: boolean;
  ltiReturnEnabled: boolean; // only ever true in the player with isLti
  showBackToSimulation: boolean;
  isLastSimulation: boolean;
}

export type ReportActionId =
  | 'sessionLogs'
  | 'viewAllReports'
  | 'editAssessment'
  | 'close'
  | 'restart'
  | 'download'
  | 'nextSimulation'
  | 'externalLink'
  | 'returnToLms';

export interface ReportAction {
  id: ReportActionId;
  label: string;
  variant: 'text' | 'stroked' | 'raised';
  // `name` is the ligature for 'mat' and the font icon class for 'fa'
  icon?: { type: 'mat' | 'fa' | 'assessment-mask' | 'assessment-img'; name?: string };
  primary?: boolean;
  badge?: string;
}

export interface ReportView {
  showHeader: boolean;
  showHelp: boolean;
  showClose: boolean; // close cross in the top-right corner, not counted as an action
  header: ReportAction[];
  footer: ReportAction[];
}

export function getReportContext(inputs: {
  dialogMode?: boolean;
  fromEditorContext?: boolean;
}): ReportContext {
  if (inputs.dialogMode) return 'editor';
  if (inputs.fromEditorContext) return 'program-launcher';
  return 'player';
}

// Player outside of an LMS launch: learner-facing report (indicative score, Labs learner actions)
export const isRegularPlayer = (f: Pick<ReportFacts, 'context' | 'isLti'>): boolean =>
  f.context === 'player' && !f.isLti;

export function getReportView(f: ReportFacts): ReportView {
  return {
    showHeader: f.context !== 'program-launcher',
    showHelp: f.context === 'editor',
    showClose: f.isSimulation && f.context === 'editor',
    ...(f.isSimulation ? getProButtons(f) : getLabsButtons(f))
  };
}

/* Pro */

// Figma "Report" component: End Sim = player, During Sim = program-launcher, Editor Report = editor
// Secondary actions are outlined, main actions are filled
// The main action of the player is always labelled CONTINUE, whatever it does (next simulation, launcher, LMS)
const PRO: Record<'download' | 'openLogs' | 'restart' | 'continue' | 'returnToLms', ReportAction> = {
  download: { id: 'download', variant: 'stroked', label: 'DOWNLOAD REPORT', icon: { type: 'fa', name: 'fa-download' } },
  openLogs: { id: 'sessionLogs', variant: 'raised', label: 'OPEN LOGS', icon: { type: 'mat', name: 'list' }, primary: true },
  restart: { id: 'restart', variant: 'stroked', label: 'RESTART', icon: { type: 'fa', name: 'fa-refresh' } },
  continue: { id: 'nextSimulation', variant: 'raised', label: 'CONTINUE', primary: true },
  returnToLms: { id: 'returnToLms', variant: 'raised', label: 'CONTINUE', primary: true }
};

function getProButtons(f: ReportFacts): Pick<ReportView, 'header' | 'footer'> {
  switch (f.context) {
    case 'editor':
      // While loading or in error, only the close cross is available
      return { header: [], footer: f.hasResult ? [PRO.download, PRO.openLogs] : [] };
    case 'player':
      // Review of a past report (disableContinue=true): nothing to restart or continue
      if (f.readOnly) {
        return { header: [], footer: [] };
      }
      if (f.isLti) {
        // CONTINUE goes back to the LMS, only when the hub allows it
        return { header: [], footer: f.ltiReturnEnabled ? [PRO.restart, PRO.returnToLms] : [PRO.restart] };
      }
      // CONTINUE goes to the next simulation, or back to the program launcher after the last simulation
      // or when the simulation was started from the launcher (see showBackToSimulation / isLastSimulation)
      return { header: [], footer: [PRO.restart, PRO.continue] };
    case 'program-launcher':
      // Read-only: going back is the launcher's own arrow next to the title
      return { header: [], footer: [] };
  }
}

/* Labs */

type Rule = ReportAction & { show: boolean };

const visible = (rules: Rule[]): ReportAction[] =>
  rules.filter(rule => rule.show).map(({ show, ...action }) => action);

// Order = display order
function getLabsButtons(f: ReportFacts): Pick<ReportView, 'header' | 'footer'> {
  const isEditor = f.context === 'editor';
  const isLearner = isRegularPlayer(f);
  // Contributor: header actions once the assessment editor was opened (no footer EDIT from the player anymore)
  const headerEdit = isLearner && f.canEdit && f.hasClickedAssessmentButton;
  // The external link CONTINUE takes the primary color when present
  const externalLink = isLearner && f.hasExternalLink;

  return {
    header: visible([
      { show: isEditor && f.hasResult, id: 'sessionLogs', variant: 'stroked', label: 'SHOW USER SESSION LOGS',
        icon: { type: 'mat', name: 'remove_red_eye' } },
      { show: headerEdit, id: 'viewAllReports', variant: 'stroked', label: 'VIEW ALL REPORTS',
        icon: { type: 'mat', name: 'remove_red_eye' } },
      { show: headerEdit, id: 'editAssessment', variant: 'stroked', label: 'EDIT ASSESSMENT',
        icon: { type: 'assessment-mask' }, badge: 'New' }
    ]),
    footer: visible([
      { show: isEditor, id: 'close', variant: 'text', label: 'CLOSE', primary: true },
      { show: isLearner, id: 'restart', variant: 'raised', label: 'RESTART',
        icon: { type: 'mat', name: 'replay' } },
      { show: f.hasResult, id: 'download', variant: 'raised', label: 'DOWNLOAD REPORT',
        icon: { type: 'fa', name: 'fa-download' }, primary: !externalLink },
      { show: externalLink, id: 'externalLink', variant: 'raised', label: 'CONTINUE', primary: true },
      { show: f.ltiReturnEnabled, id: 'returnToLms', variant: 'raised', label: 'RETURN TO LMS', primary: true }
    ])
  };
}
